import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Ruler, ImageSquare, Plus, Trash, CheckCircle, X, MagnifyingGlassPlus,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * CNCCuttingSizesPanel — admin-only panel that appears inside
 * OrderDetailDialog for gate/railing orders after the fabricator has
 * uploaded sheet-size photos (status = size_uploaded or later).
 *
 * Owner reads the uploaded photos, then enters the actual CNC cutting parts
 * (Part / W / H / Qty). System auto-computes sqft per part and total.
 * Customer's original W × H × qty is NEVER overwritten (it lives in
 * `items[]`; CNC parts live in `cnc_cutting_parts[]`).
 */
export default function CNCCuttingSizesPanel({ order, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(null);       // {url, name}
  const [newPart, setNewPart] = useState({ part_name: "", width_in: "", height_in: "", quantity: 1, unit: "sqft" });
  const token = typeof window !== "undefined" ? localStorage.getItem("sc_token") : null;

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${order.id}/cnc-cutting-parts`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id, order.status, (order.size_photos || []).length]);

  const parts = data?.cnc_cutting_parts || [];
  const totalSqft = data?.total_cnc_sqft || 0;
  const photos = data?.size_photos || [];
  const isConfirmed = order.status === "size_confirmed";
  const partPreviewSqft = useMemo(() => {
    const w = Number(newPart.width_in) || 0;
    const h = Number(newPart.height_in) || 0;
    const q = Number(newPart.quantity) || 0;
    return Math.round(((w * h * q) / 144) * 1000) / 1000;
  }, [newPart]);

  const addPart = async (e) => {
    e?.preventDefault();
    if (!newPart.part_name.trim()) { toast.error("Part name daaliye"); return; }
    if (!(Number(newPart.width_in) > 0) || !(Number(newPart.height_in) > 0)) {
      toast.error("Width/Height > 0 daaliye"); return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/cnc-cutting-parts`, {
        part_name: newPart.part_name.trim(),
        width_in: Number(newPart.width_in),
        height_in: Number(newPart.height_in),
        quantity: Number(newPart.quantity) || 1,
        unit: newPart.unit,
      });
      setNewPart({ part_name: "", width_in: "", height_in: "", quantity: 1, unit: "sqft" });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const removePart = async (id) => {
    if (!window.confirm("Ye part delete karein?")) return;
    setBusy(true);
    try {
      await api.delete(`/orders/${order.id}/cnc-cutting-parts/${id}`);
      load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const confirmCutting = async () => {
    if (!parts.length) { toast.error("Kam se kam ek part daaliye"); return; }
    if (!window.confirm("Ye cutting sizes confirm karein? Iske baad design stage me chala jaayega.")) return;
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/cnc-cutting-parts/confirm`);
      toast.success("Cutting size confirmed ✓ — order design stage me");
      load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="surface p-4 rounded-sm border-l-4 border-amber-500/60" data-testid="cnc-cutting-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs font-mono-num tracking-widest text-amber-300">
          <Ruler size={14} weight="fill" />
          // CNC CUTTING SIZES
          {isConfirmed && (
            <span className="text-emerald-400 text-[10px] tracking-widest bg-emerald-500/15 border border-emerald-500/40 px-1.5 py-0.5 rounded-sm ml-1">
              ✓ CONFIRMED
            </span>
          )}
        </div>
        <div className="text-[10px] font-mono-num tracking-widest text-slate-400">
          CUSTOMER: <span className="text-cyan-300 font-bold">{fmt(data?.customer_sqft)} sqft</span>
          {" · "}
          CNC TOTAL: <span className="text-amber-300 font-bold">{fmt(totalSqft)} sqft</span>
        </div>
      </div>

      {/* Size photos from fabricator */}
      {photos.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mb-1.5">
            SIZE PHOTOS FROM FABRICATOR ({photos.length})
          </div>
          <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
            {photos.map((p) => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/size-photos/${p.id}/download?auth=${token || ""}`;
              return (
                <button
                  key={p.id}
                  onClick={() => setZoom({ url, name: p.original_filename || "size", note: p.note || "" })}
                  className="relative border border-slate-700 hover:border-amber-500/60 rounded-sm overflow-hidden aspect-square group"
                  data-testid={`cnc-size-photo-${p.id}`}
                >
                  <img src={url} alt={p.original_filename || "size"} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/40 flex items-center justify-center transition-colors">
                    <MagnifyingGlassPlus size={20} className="text-white opacity-0 group-hover:opacity-100" weight="bold" />
                  </div>
                  {p.note && (
                    <div className="absolute bottom-0 inset-x-0 bg-slate-950/80 text-white text-[9px] px-1 py-0.5 truncate">
                      {p.note}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Parts table */}
      <div className="mt-3">
        <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mb-1.5">
          CUTTING PARTS ({parts.length})
        </div>
        {parts.length === 0 ? (
          <div className="text-slate-500 text-xs italic bg-slate-800/30 rounded-sm p-3 text-center">
            Koi part nahi daala — size photos padho aur niche add karo.
          </div>
        ) : (
          <div className="rounded-sm border border-slate-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
                <tr>
                  <th className="text-left px-3 py-2">PART</th>
                  <th className="text-right px-3 py-2">W (in)</th>
                  <th className="text-right px-3 py-2">H (in)</th>
                  <th className="text-right px-3 py-2">QTY</th>
                  <th className="text-right px-3 py-2">SQFT</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {parts.map((p) => (
                  <tr key={p.id} className="border-t border-slate-800" data-testid={`cnc-part-row-${p.id}`}>
                    <td className="px-3 py-2 text-slate-200">{p.part_name}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-slate-300">{p.width_in}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-slate-300">{p.height_in}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-slate-300">{p.quantity}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-amber-300 font-bold">{fmt(p.sqft)}</td>
                    <td className="px-2 py-2 text-right">
                      {!isConfirmed && (
                        <button
                          onClick={() => removePart(p.id)}
                          className="text-slate-500 hover:text-red-400 p-1"
                          data-testid={`cnc-part-del-${p.id}`}
                        >
                          <Trash size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-slate-700 bg-slate-800/30">
                  <td colSpan={4} className="px-3 py-2 text-right text-[10px] font-mono-num tracking-widest text-slate-400">
                    TOTAL CNC CUTTING SQFT
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num text-amber-400 font-black text-base">
                    {fmt(totalSqft)}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add part form + Confirm button */}
      {!isConfirmed && (
        <>
          <form
            onSubmit={addPart}
            className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-end bg-slate-800/30 rounded-sm p-3"
            data-testid="cnc-part-add-form"
          >
            <div className="col-span-2">
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">PART NAME *</Label>
              <Input
                value={newPart.part_name}
                onChange={(e) => setNewPart({ ...newPart, part_name: e.target.value })}
                placeholder="Left Panel"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1"
                data-testid="cnc-part-name"
              />
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">W (in) *</Label>
              <Input
                type="number" min="0" step="0.1"
                value={newPart.width_in}
                onChange={(e) => setNewPart({ ...newPart, width_in: e.target.value })}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1 font-mono-num"
                data-testid="cnc-part-w"
              />
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">H (in) *</Label>
              <Input
                type="number" min="0" step="0.1"
                value={newPart.height_in}
                onChange={(e) => setNewPart({ ...newPart, height_in: e.target.value })}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1 font-mono-num"
                data-testid="cnc-part-h"
              />
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">QTY</Label>
              <Input
                type="number" min="1" step="1"
                value={newPart.quantity}
                onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1 font-mono-num"
                data-testid="cnc-part-qty"
              />
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">SQFT (preview)</Label>
              <div className="bg-slate-900/60 border border-emerald-500/40 text-emerald-300 h-10 rounded-sm mt-1 font-mono-num font-bold px-2 flex items-center justify-end"
                   data-testid="cnc-part-sqft-preview">
                {fmt(partPreviewSqft)}
              </div>
            </div>
            <div className="col-span-2 md:col-span-6 flex justify-end">
              <Button
                type="submit"
                disabled={busy}
                className="btn-industrial rounded-sm h-10 px-4"
                data-testid="cnc-part-add-btn"
              >
                <Plus size={14} className="mr-1" /> ADD PART
              </Button>
            </div>
          </form>

          {parts.length > 0 && (
            <div className="mt-3 flex justify-end">
              <Button
                onClick={confirmCutting}
                disabled={busy}
                className="rounded-sm h-11 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-heading tracking-widest"
                data-testid="cnc-confirm-btn"
              >
                <CheckCircle size={16} weight="fill" className="mr-2" />
                CONFIRM CUTTING SIZE
              </Button>
            </div>
          )}
        </>
      )}

      {/* Zoom modal */}
      {zoom && (
        <div
          className="fixed inset-0 z-[80] bg-slate-950/90 flex items-center justify-center p-4"
          onClick={() => setZoom(null)}
          data-testid="cnc-size-photo-zoom"
        >
          <button
            className="absolute top-4 right-4 text-white p-2 hover:bg-slate-800 rounded-sm"
            onClick={() => setZoom(null)}
            data-testid="cnc-size-photo-zoom-close"
          >
            <X size={20} weight="bold" />
          </button>
          <div className="max-w-4xl max-h-[85vh] flex flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <img src={zoom.url} alt={zoom.name} className="max-w-full max-h-[75vh] object-contain rounded-sm" />
            {zoom.note && (
              <div className="text-slate-300 text-sm font-mono-num bg-slate-900/70 px-3 py-1 rounded-sm">
                {zoom.note}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
